import { useMemo } from 'react';
import type { Choice, TestResult as TestResultType } from '../../types/test';
import { TestChart } from './TestChart';
import { QuestionHtmlContent } from './QuestionHtmlContent';

export interface TestResultProps {
  choices: Choice[];
  selectedChoiceId: number | null;
  chartStyle: 0 | 1 | 2;
  results?: TestResultType[];
  showExplanation?: boolean;
  isExplaining?: boolean;
  formatType?: 'question' | 'example' | 'survey';
  /** @deprecated 回答表示は selectedChoiceId（ストア＝サーバー同期済み）を正とするため未使用 */
  lectureId?: string | undefined;
  /** @deprecated 同上 */
  userId?: string | undefined;
  /** @deprecated 同上 */
  questionId?: number;
  /** 描画するパーツを指定。'summary'=ステータス+KPI+グラフのみ, 'choices'=選択肢のみ, 'all'=全て（デフォルト） */
  part?: 'all' | 'summary' | 'choices';
  /** 問題のステータス（not_startedの場合は出題されていないと判断） */
  questionStatus?: string;
}

/**
 * Get the correct choice from the choices array.
 */
function getCorrectChoice(choices: Choice[]): Choice | undefined {
  return choices.find((c) => c.is_correct);
}

/**
 * TestResult - Displays test result with correct/incorrect indication and explanation.
 *
 * Features:
 * - Shows whether the user's answer was correct or incorrect
 * - Displays the correct answer
 * - Shows explanation for the correct answer
 * - Optionally displays answer distribution chart
 *
 * Validates: Requirements 6.5, 6.6
 */
export function TestResult({
  choices,
  selectedChoiceId,
  chartStyle,
  results = [],
  isExplaining = false,
  formatType = 'question',
  part = 'all',
  questionStatus,
}: TestResultProps) {
  // 回答はストア経由で渡される selectedChoiceId を正とする。
  // ストアはページアクセス時・再同期時にサーバーの回答履歴（/answers/history）と
  // 突き合わせ済みのため、localStorage を直接参照しない（サーバーが正）。
  // 以前は localStorage を優先していたが、管理画面の「中止（回答リセット）」を
  // 受信できなかった場合に削除済みの旧回答が正解表示される問題があったため廃止した。
  const actualChoiceId = selectedChoiceId;

  const correctChoice = useMemo(() => getCorrectChoice(choices), [choices]);
  const hasAnswer = actualChoiceId !== null;
  const isSurvey = formatType === 'survey';

  // 正答率を計算（resultsから）
  // 母数は「実際に回答した数」（未回答 choiceId=0 は分母に含めない）。
  // 管理画面・集計API（correct_count / total_answers）と同一定義に統一（G3対応）
  const correctResult = results.find((r) => r.choiceId === correctChoice?.id);
  const answeredTotal = results
    .filter((r) => r.choiceId !== 0)
    .reduce((sum, r) => sum + r.count, 0);
  const correctRate = answeredTotal > 0 && correctResult
    ? (correctResult.count / answeredTotal) * 100
    : 0;

  // 選択した選択肢のインデックスを取得（表示用）
  const selectedChoiceIndex = actualChoiceId !== null
    ? (() => {
        const index = choices.findIndex((c) => c.id === actualChoiceId);
        return index >= 0 ? index + 1 : null;
      })()
    : null;
  const correctChoiceIndex = correctChoice
    ? choices.findIndex((c) => c.id === correctChoice.id) + 1
    : null;

  const sortedChoices = useMemo(
    () => [...choices].sort((a, b) => a.sort_key - b.sort_key),
    [choices],
  );

  // 出題されていない問題かどうかを判定
  // status が "not_started" の場合は出題されていない
  const isNotPublished = questionStatus === 'not_started';

  const summaryBlock = (part === 'all' || part === 'summary') && (
    <>
      {/* 正誤判定 or 時間切れ表示（talliedステータス=解説中の場合、アンケート以外） */}
      {/* ただし、出題されていない問題（status="not_started"）では時間切れを表示しない */}
      {isExplaining && !isSurvey && !isNotPublished && (
        <div className="result-messages-row">
          {hasAnswer ? (
            <div className={`result-message-box ${
              correctChoice && actualChoiceId === correctChoice.id
                ? 'result-correct' : 'result-incorrect'
            }`}>
              <span>
                {correctChoice && actualChoiceId === correctChoice.id ? '正解!' : '不正解'}
              </span>
            </div>
          ) : (
            <div className="result-message-box result-neutral">
              <span className="result-message-icon">
                <svg className="hi-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                  <text x="12" y="16" textAnchor="middle" fontSize="12" fill="currentColor">◎</text>
                </svg>
              </span>
              <span>時間切れ</span>
            </div>
          )}
        </div>
      )}

      <div className="result-info-boxes">
        <div className="result-info-box result-info-box-user">
          <div className="result-info-label">あなたの解答</div>
          <div className="result-info-value">
            {hasAnswer ? `${selectedChoiceIndex}` : (isNotPublished ? '実施データなし' : '未解答')}
          </div>
        </div>
        {!isSurvey && correctChoiceIndex !== null && (
          <div className="result-info-box result-info-box-correct">
            <div className="result-info-label">正解は</div>
            <div className="result-info-value">{correctChoiceIndex}</div>
          </div>
        )}
        {!isSurvey && (
          <div className="result-info-box result-info-box-rate">
            <div className="result-info-label">正答率</div>
            <div className="result-info-value">
              {results.length > 0 ? `${Math.round(correctRate)}%` : '-'}
            </div>
          </div>
        )}
      </div>

      <div className="result-chart-section">
        <TestChart
          chartStyle={chartStyle}
          choices={choices}
          results={results}
        />
      </div>
    </>
  );

  const choicesBlock = (part === 'all' || part === 'choices') && (
    <div className="test-result-explanation-area">
      <div className="test-options-result">
        {sortedChoices.map((choice) => {
          const isSelected = choice.id === actualChoiceId;
          const isCorrectChoice = choice.is_correct;
          const choiceIndex = sortedChoices.findIndex((c) => c.id === choice.id) + 1;

          return (
            <label
              key={choice.id}
              className={`test-option test-option-result ${
                isSurvey
                  ? ''
                  : (isCorrectChoice
                      ? 'result-option-correct'
                      : isSelected
                        ? 'result-option-user'
                        : '')
              }`}
            >
              <div className="test-option-result-text">
                <span className="test-option-result-index">{choiceIndex}.</span>{' '}
                <QuestionHtmlContent html={choice.choice} className="test-html-content test-html-inline" as="span" />
              </div>
              {choice.description && (
                <QuestionHtmlContent
                  html={choice.description}
                  className="result-option-explanation test-html-content"
                />
              )}
            </label>
          );
        })}
      </div>
    </div>
  );

  if (part === 'summary') {
    return <div className="test-result">{summaryBlock}</div>;
  }
  if (part === 'choices') {
    return <>{choicesBlock}</>;
  }
  return (
    <div className="test-result">
      {summaryBlock}
      {choicesBlock}
    </div>
  );
}
